import { Schema as S } from "effect";
import { defineMessageUnion } from "foldkit/message";
import * as Relationships from "../../widgets/relationships";

import { getEntries } from "../../api/commands/entries";
import { setCadence } from "../../api/commands/keepInTouch";
import { getBacklinks } from "../../api/commands/notes";
import {
	checkProfileImageExists,
	editRecord,
	getRecord,
	getRecordHistory,
	setRecordAgentLink,
	uploadProfileImage,
} from "../../api/commands/records";
import { listTasks } from "../../api/commands/tasks";
import { ApiError } from "../../api/errors";
import { Entry } from "../../api/schemas/entries";
import { Cadence } from "../../api/schemas/records";
import * as AddEntry from "../../widgets/addEntry";
import * as Attributes from "../../widgets/attributes";
import { Message as CropMessage } from "../../widgets/cropper";
import * as Tags from "../../widgets/tags";
import { Tab } from "./model";

export const Message = defineMessageUnion({
	GotRelationshipsMessage: { message: Relationships.Message },
	InteractionLogged: { nodeId: S.String },
	// The root re-synced Shared (flag or features settled) while this page stayed.
	ChangedShared: {},
	// Completions carry the record they were issued for: a late result for the
	// previous record must not land on the page after the route changed.
	CompletedFetchRecord: {
		nodeId: S.String,
		asOf: S.Option(S.String),
		generation: S.Number,
		result: getRecord.Result,
	},
	CompletedFetchEntries: {
		nodeId: S.String,
		cursor: S.Number,
		asOf: S.Option(S.String),
		generation: S.Number,
		result: getEntries.Result,
	},
	CompletedCheckImage: {
		nodeId: S.String,
		generation: S.Number,
		result: checkProfileImageExists.Result,
	},
	ClickedLoadMoreEntries: {},
	SelectedTab: { tab: Tab },
	CompletedFetchHistory: {
		nodeId: S.String,
		cursor: S.Number,
		generation: S.Number,
		result: getRecordHistory.Result,
	},
	ClickedLoadMoreHistory: {},
	ToggledHistoryCard: { id: S.String },
	ClickedViewAt: { validFrom: S.String },
	CompletedNavigate: {},
	ClickedEdit: {},
	ChangedName: { value: S.String },
	ChangedSubtitle: { value: S.String },
	ChangedEmail: { value: S.String },
	ChangedDescription: { value: S.String },
	GotTagsMessage: { message: Tags.Message },
	ClickedRemoveTag: { tag: S.String },
	GotAttributesMessage: { message: Attributes.Message },
	ClickedDismissError: {},
	SubmittedEdit: {},
	CompletedEditRecord: { generation: S.Number, result: editRecord.Result },
	ClickedChangeImage: {},
	ClickedCloseImageModal: {},
	ChangedImageFile: { file: S.Option(S.instanceOf(File)) },
	GotCropMessage: { message: CropMessage },
	CompletedPreview: {},
	ClickedBackToCrop: {},
	ClickedUpload: {},
	CompletedUpload: { generation: S.Number, result: uploadProfileImage.Result },
	GotAddEntryMessage: { message: AddEntry.Message },
	CompletedEditorAction: {},
	CompletedTrack: {},
	CompletedAddEntries: {
		nodeId: S.String,
		generation: S.Number,
		result: S.Result(S.Array(Entry), ApiError),
	},
	// U8: cadence and pause are their own write, not part of the edit form (R19).
	ChangedCadence: { cadence: S.NullOr(Cadence) },
	ClickedPause: {},
	CompletedSetCadence: {
		nodeId: S.String,
		generation: S.Number,
		result: setCadence.Result,
	},
	// L5: someone else's agent link on this person, its own write like cadence.
	ChangedAgentLink: { value: S.String },
	SubmittedAgentLink: {},
	ClickedClearAgentLink: {},
	CompletedSetAgentLink: {
		nodeId: S.String,
		generation: S.Number,
		result: setRecordAgentLink.Result,
	},
	// U12: the person's Tasks and Notes tabs (live data, generation-checked).
	CompletedFetchPersonTasks: {
		nodeId: S.String,
		generation: S.Number,
		/** Clock time of the read, so the view can mark overdue tasks. */
		now: S.Number,
		result: listTasks.Result,
	},
	CompletedFetchBacklinks: {
		nodeId: S.String,
		generation: S.Number,
		result: getBacklinks.Result,
	},
});
export type Message = typeof Message.Type;
